import { Box, Theme, Typography, useMediaQuery } from '@mui/material'
import { AppBar, TitlePortal } from 'react-admin'
import { AppBarToolbar } from './AppBarToolbar'
import Logo from './Logo'
import { MyUserMenu } from './MyUserMenu'

const CustomAppBar = () => {
  const isLargeEnough = useMediaQuery<Theme>((theme) => theme.breakpoints.up('sm'))
  return (
    <AppBar
      toolbar={<AppBarToolbar />}
      userMenu={<MyUserMenu />}
      sx={{
        bgcolor: 'white',
        color: 'text.primary',
        borderBottom: '1px solid #e0e0e0',
        boxShadow: 'none',
        '& .RaAppBar-toolbar': {
          // Trim the horizontal padding on phones so the right-hand controls
          // have a little more room; full padding returns at sm+. (Part of the
          // phone AppBar-overflow fix — see AppBarToolbar.tsx.)
          px: { xs: 1, sm: 2 },
        },
        // Top-bar user menu: show the avatar/account icon only — hide the name
        // label. The logged-in vet's name is already shown in the dashboard
        // header, so React Admin's UserMenu (which prints the name next to the
        // avatar) was showing it twice in the same corner; the long name also
        // pushed the dropdown off-screen. Collapsing the button's text node
        // leaves a clean icon-only trigger (MUI fixes the icon size via
        // .MuiButton-startIcon, so it is unaffected by fontSize: 0). The name
        // still appears inside the menu and on the dashboard.
        '& .RaUserMenu-userButton': {
          minWidth: 0,
          fontSize: 0,
          '& .MuiButton-startIcon': { marginRight: 0 },
        },
      }}
      elevation={0}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          // Phones only: the toolbar (menu, logo, alerts, language, unit,
          // account) overflowed a 360-390px viewport by 3-33px, clipping the
          // account button. sm+ keeps the 130px logo.
          '@media (max-width:599.95px)': { '& img': { width: 96, height: 'auto' } },
        }}
      >
        <Logo width={130} height={30} />
        <Typography
          variant="h6"
          sx={{
            // Match the "Adapet" wordmark color so logo + "Medical" read as one banner line
            color: '#4e4e4e',
            fontWeight: 600,
            display: { xs: 'none', md: 'block' },
            letterSpacing: '-0.02em',
          }}
        >
          Medical
        </Typography>
      </Box>
      {/* The page title used to butt straight up against the "Medical"
          wordmark in the same weight, so screens read as "Medical Notes",
          "Medical Care Teams", "Medical VetPo Help Center". A hairline rule
          and a quieter weight make it brand | location. :empty keeps the
          rule off pages that set no title (the dashboard). */}
      <TitlePortal
        sx={{
          fontSize: '1rem',
          fontWeight: 500,
          color: 'text.secondary',
          // Spacing only when there IS a title: an unconditional margin
          // pushed the phone toolbar 16px past the viewport.
          '&:not(:empty)': {
            ml: 2,
            pl: 2,
            borderLeft: '1px solid',
            borderColor: 'divider',
            lineHeight: '28px',
          },
        }}
      />
      {isLargeEnough && <Box component="span" sx={{ flex: 1 }} />}
    </AppBar>
  )
}

export default CustomAppBar
